import { IS_PRODUCTION } from '../common/constants/constants'
import { shouldDisableCostlyVendors } from '../flags/utils'

// ============================================================================
// Types & Declarations
// ============================================================================

/**
 * Freshpaint SDK initialization options.
 */
export interface FreshpaintInitOptions {
  /** When true, prevents capturing text content of clicked elements (HIPAA compliance) */
  disable_target_text_capture?: boolean
}

/**
 * TypeScript declarations for the Freshpaint SDK global object.
 * These methods are available after the SDK script loads.
 */
declare global {
  interface Window {
    freshpaint?: {
      __SV?: number
      __loaded?: boolean
      _i?: Array<[string, FreshpaintInitOptions?, string?]>
      init: (envId: string, options?: FreshpaintInitOptions) => void
      track: (event: string, properties?: Record<string, unknown>) => void
      identify: (userId: string, traits?: Record<string, unknown>) => void
      page: (name?: string, properties?: Record<string, unknown>) => void
      register: (properties: Record<string, unknown>) => void
      reset: () => void
      push: (args: unknown[]) => void
    }
  }
}

/**
 * Hook type for loading Freshpaint SDK.
 * Returns whether the SDK is ready to use.
 */
export type UseFreshpaint = (enabled: boolean) => boolean

// ============================================================================
// Constants
// ============================================================================

/**
 * Freshpaint Environment ID - obtained from the Freshpaint dashboard (https://app.freshpaint.io/sources)
 * Loaded from environment variable
 */
export const FRESHPAINT_ENV_ID = process.env.FRESHPAINT_ENV_ID ?? ''

/**
 * Freshpaint SDK URL - uses the official Freshpaint CDN
 * Format: https://freshpaint-cdn.com/js/{ENV_ID}/freshpaint.js
 */
export const FRESHPAINT_SDK_URL = `https://freshpaint-cdn.com/js/${FRESHPAINT_ENV_ID}/freshpaint.js`

// ============================================================================
// Initialization
// ============================================================================

/**
 * Extended array type for Freshpaint command queue.
 * The official Freshpaint pattern adds properties directly to the array.
 */
type FreshpaintQueueArray = unknown[] & {
  __loaded?: boolean
  config?: boolean
  __SV?: number
  _i?: Array<[string, FreshpaintInitOptions?, string?]>
}

/**
 * Initializes the Freshpaint command queue proxy.
 * This sets up a Proxy object that queues method calls (track, identify, page, etc.)
 * before the actual SDK script loads. Once the SDK loads, it processes the queued commands.
 *
 * This is the official Freshpaint initialization pattern that:
 * 1. Prevents race conditions when calling methods before SDK loads
 * 2. Supports the Freshpaint visual editor (fpState hash parsing)
 * 3. Queues all method calls for deferred execution
 *
 * When the SDK loads, it drains commands by iterating over the array itself.
 *
 * @param envId - Freshpaint environment ID
 * @param options - Optional initialization options
 */
export const initializeFreshpaintQueue = (envId: string, options?: FreshpaintInitOptions): void => {
  if (typeof window === 'undefined') {
    return
  }

  // Skip if already initialized
  if (window.freshpaint?.__SV) {
    return
  }

  // Get existing queue array or create new one
  const freshpaintArray: FreshpaintQueueArray = Array.isArray(window.freshpaint)
    ? (window.freshpaint as FreshpaintQueueArray)
    : []

  // Handle visual editor state from URL hash
  try {
    const hash = window.location.hash
    if (hash) {
      const fpStateMatch = hash.match(/fpState=([^&]*)/)
      if (fpStateMatch) {
        const fpState = JSON.parse(decodeURIComponent(fpStateMatch[1]))
        if (fpState.action === 'fpeditor') {
          window.sessionStorage.setItem('_fpcehash', hash)
          history.replaceState(
            fpState.desiredHash || '',
            document.title,
            window.location.pathname + window.location.search,
          )
        }
      }
    }
  } catch {
    // Ignore hash parsing errors
  }

  // Add required properties directly to the array (official Freshpaint pattern)
  freshpaintArray.__loaded = false
  freshpaintArray.config = false
  freshpaintArray.__SV = 2

  // Wrap the array in a Proxy to intercept method calls
  // The Proxy dynamically provides init, track, identify, page, etc. via the get handler
  window.freshpaint = new Proxy(freshpaintArray, {
    get: function (target, prop: string) {
      // Return existing properties if they exist
      if (target[prop as keyof typeof target] !== undefined) {
        return target[prop as keyof typeof target]
      }
      // Handle init specially
      if (prop === 'init') {
        return function (id: string, opts?: FreshpaintInitOptions, name?: string) {
          target._i = target._i || []
          target._i.push([id, opts || {}, name || 'freshpaint'])
        }
      }
      // All other methods queue the call by pushing to the array
      return function (...args: unknown[]) {
        const queuedCall = [prop, ...args]
        target.push(queuedCall)
        return new Proxy(queuedCall, {
          get: function (callTarget, callProp: string) {
            if (callTarget[callProp as keyof typeof callTarget]) {
              return callTarget[callProp as keyof typeof callTarget]
            }
            return function (...chainArgs: unknown[]) {
              callTarget.length = 0
              callTarget.push([prop, ...args])
              callTarget.push([callProp, ...chainArgs])
            }
          },
        })
      }
    },
  }) as unknown as Window['freshpaint']

  window.freshpaint?.init(envId, options)
}

// ============================================================================
// Route Allowlist
// ============================================================================

const FRESHPAINT_ALLOWED_ROUTES = [
  // Unauthenticated routes
  '/',
  '/social',
  '/pathways/care-options',
  '/wellness-check-in/*', // Matches /wellness-check-in, and subroutes (/wellness-check-in/1, /wellness-check-in/preview, etc.)
  '/pathways/self-care-options',
  '/pathways/essentials-homepage-widget',
  '/login',
  // Registration routes
  '/register/connect',
  '/secure/index/account-info/background-info',
  // Authenticated routes
  '/secure/index/search-care',
] as const

/**
 * Checks if the given pathname is in the Freshpaint allowed routes list.
 *
 * @param pathname - The current route pathname (query params are ignored)
 * @returns Whether Freshpaint tracking is allowed on this route
 */
const isFreshpaintEnabledRoute = (pathname: string): boolean => {
  if (!pathname) return false

  return FRESHPAINT_ALLOWED_ROUTES.some((route) => {
    if (route.endsWith('/*')) {
      // Prefix matching for wildcard routes
      const prefix = route.slice(0, -2) // Remove '/*'
      return pathname === prefix || pathname.startsWith(`${prefix}/`)
    }
    // Exact matching for non-wildcard routes
    return pathname === route
  })
}

// ============================================================================
// Utilities
// ============================================================================

/**
 * Determines whether Freshpaint SDK should be initialized.
 * Checks feature flag, environment, and test conditions.
 *
 * @param enabled - Feature flag value (isFreshpaintEnabled)
 * @returns Whether Freshpaint should be loaded
 */
export const shouldInitializeFreshpaint = (enabled: boolean): boolean => {
  if (!enabled) {
    return false
  }
  if (!IS_PRODUCTION && process.env.NODE_ENV !== 'staging') {
    return false
  }
  if (!FRESHPAINT_ENV_ID) {
    console.error('[Freshpaint] FRESHPAINT_ENV_ID is not configured. SDK will not be initialized.')
    return false
  }
  if (shouldDisableCostlyVendors) {
    return false
  }
  if (typeof window !== 'undefined' && window.Cypress) {
    return false
  }
  return true
}

// ============================================================================
// Network Interception (Route Blocking)
// ============================================================================

interface XMLHttpRequestWithUrl extends XMLHttpRequest {
  _freshpaintUrl?: string
}

let isRouteBlockingEnabled = false
let nativeFetch: typeof fetch | null = null
let nativeXHROpen: typeof XMLHttpRequest.prototype.open | null = null
let nativeXHRSend: typeof XMLHttpRequest.prototype.send | null = null

const isFreshpaintRequest = (url: string): boolean => url.includes('perfalytics.com')

const extractUrl = (input: RequestInfo | URL): string =>
  typeof input === 'string' ? input : input instanceof URL ? input.href : input.url

/**
 * We only want to allow Freshpaint requests on Privacy / Security approved Lyra routes.
 */
const shouldBlockFreshpaintRequest = (url: string): boolean => {
  return isFreshpaintRequest(url) && !isFreshpaintEnabledRoute(window.location.pathname)
}

/**
 * Enables route-based blocking for Freshpaint tracking.
 * Intercepts fetch and XMLHttpRequest to block Freshpaint requests at the network level.
 */
export const enableFreshpaintRouteBlocking = (): void => {
  if (typeof window === 'undefined' || isRouteBlockingEnabled) return

  nativeFetch = window.fetch.bind(window)
  window.fetch = (input: RequestInfo | URL, init?: RequestInit): Promise<Response> => {
    if (shouldBlockFreshpaintRequest(extractUrl(input))) {
      return Promise.resolve(new Response('{}', { status: 200 }))
    }
    return nativeFetch!(input, init)
  }

  nativeXHROpen = XMLHttpRequest.prototype.open
  XMLHttpRequest.prototype.open = function (
    this: XMLHttpRequestWithUrl,
    method: string,
    url: string | URL,
    async: boolean = true,
    username?: string | null,
    password?: string | null,
  ) {
    const resolvedUrl = typeof url === 'string' ? url : url.href
    this._freshpaintUrl = resolvedUrl
    return nativeXHROpen!.call(this, method, resolvedUrl, async, username, password)
  }

  nativeXHRSend = XMLHttpRequest.prototype.send
  XMLHttpRequest.prototype.send = function (
    this: XMLHttpRequestWithUrl,
    body?: Document | XMLHttpRequestBodyInit | null,
  ) {
    if (shouldBlockFreshpaintRequest(this._freshpaintUrl ?? '')) {
      return
    }
    return nativeXHRSend!.call(this, body)
  }

  isRouteBlockingEnabled = true
}
